import * as React from 'react';
// eslint-disable-next-line import/no-cycle
import { Artikel, getArtikel } from 'api/artikel';
import { ArtikelDetailKalkulation, getArtikelDetailKalkulationen } from 'api/artikelDetailKalkulation';
import { useSelector } from 'react-redux';
// eslint-disable-next-line import/no-cycle
import { einheitenByCodeSelector } from 'store/lookups/selector';

export interface ArtikelCalculator {
    getPreise: (
        artikelGruppeNr: number | undefined,
        artikelNr: string | undefined,
        einheit: string | undefined,
    ) => Promise<{ ePreis: number; ekPreis: number }>;
    convertPreisByEinheit: (
        artikel: Artikel,
        valueFrom: number,
        einhFrom?: string,
        einhTo?: string,
    ) => {
        value: number;
        factor: number;
    };
}

export default function useArtikelCalculator(): ArtikelCalculator {
    const [lastArtikelDetailKalkulationen, setLastArtikelDetailKalkulationen] = React.useState<
        ArtikelDetailKalkulation[] | null
    >(null);
    const einheitenByCode = useSelector(einheitenByCodeSelector);

    const getPreise = React.useCallback(
        async (artikelGruppeNr: number | undefined, artikelNr: string | undefined, einheit: string | undefined) => {
            if (!artikelGruppeNr || !artikelNr || !einheit) {
                return { ePreis: 0, ekPreis: 0 };
            }

            let artikelDetailKalkulationen = lastArtikelDetailKalkulationen;

            if (
                artikelDetailKalkulationen?.[0]?.artikelGruppeNr !== artikelGruppeNr ||
                artikelDetailKalkulationen?.[0]?.artikelNr !== artikelNr
            ) {
                artikelDetailKalkulationen = await getArtikelDetailKalkulationen({
                    artikelGruppeNr,
                    artikelNr,
                });

                setLastArtikelDetailKalkulationen(artikelDetailKalkulationen);
            }

            const artikel = await getArtikel({ artikelNr });

            if (!artikel || !artikelDetailKalkulationen || artikelDetailKalkulationen.length === 0) {
                return { ePreis: 0, ekPreis: 0 };
            }

            let ekPreis = 0;
            let ePreis = 0;

            const defaultKalkulation = artikelDetailKalkulationen?.find((k) => k.eh === einheit);

            const ek1 = defaultKalkulation?.ek1 || 0;

            if (defaultKalkulation?.ek1Eh === 'M' && artikel.quadrmeter) {
                ePreis = ek1 * artikel.quadrmeter;
                ekPreis = ePreis;
            } else if (artikel.einheit === 'R' && defaultKalkulation?.ek1Eh === 'K' && artikel.rollenGew) {
                ePreis = ek1 * artikel.rollenGew;
                ekPreis = ePreis;
            } else {
                ePreis = defaultKalkulation?.ek1 || 0;
                ekPreis = ePreis;
            }

            return { ePreis, ekPreis };
        },
        [lastArtikelDetailKalkulationen],
    );

    const convertPreisByEinheit = React.useCallback(
        (
            artikel: Artikel,
            valueFrom: number,
            einhFrom?: string,
            einhTo?: string,
        ): {
            value: number;
            factor: number;
        } => {
            if (!einhFrom || !einhTo || !artikel) {
                return { value: 0, factor: 0 };
            }

            const einheitFrom = einheitenByCode[einhFrom];
            const einheitTo = einheitenByCode[einhTo];

            if (!einheitFrom || !einheitTo) {
                return { value: 0, factor: 0 };
            }

            if (einhFrom === einhTo) {
                return { value: valueFrom, factor: 1 };
            }

            let factor = 0;
            let valueTo = 0;

            switch (einhFrom) {
                case 'R': {
                    switch (einhTo) {
                        case 'M': // m²
                            factor = artikel.quadrmeter;
                            valueTo = valueFrom / factor;
                            break;
                        case 'Y': // 100m²
                            factor = artikel.quadrmeter * einheitTo.faktor;
                            valueTo = valueFrom / factor;
                            break;
                        case 'L': // Lfm
                        case 'E': // 100lfm
                        case 'D': // 1.000lfm
                            factor = artikel.laufmeter;
                            valueTo = (valueFrom / artikel.laufmeter) * einheitTo.faktor;
                            break;
                        case 'S': // Stück
                        case 'U': // Set
                        case 'A': // Satz
                            factor = artikel.einhProPal;
                            valueTo = valueFrom / factor;
                            break;
                        case 'T': // 1.000 Stück
                            factor = einheitTo.faktor;
                            valueTo = valueFrom / factor;
                            break;
                        default:
                            console.warn('convertPreisByEinheit.notHandled: ', einhFrom, einhTo);
                            break;
                    }
                    break;
                }
                case 'Y':
                case 'M': {
                    switch (einhTo) {
                        case 'R':
                            factor = artikel.quadrmeter * einheitTo.faktor;
                            valueTo = valueFrom * factor;
                            break;
                        default:
                            console.warn('convertPreisByEinheit.notHandled: ', einhFrom, einhTo);
                            break;
                    }
                    break;
                }
                default:
                    console.warn('convertPreisByEinheit.notHandled: ', einhFrom, einhTo);
                    break;
            }

            if (factor === 0) {
                return { value: valueTo, factor: 1 };
            }

            return { value: Math.round(valueTo * 10000) / 10000, factor };
        },
        [einheitenByCode],
    );

    return {
        getPreise,
        convertPreisByEinheit,
    };
}
